import { Button, LoadingSpinner, SegmentedControl, Tabs, TextLink, Tooltip, Typography } from '@neo4j-ndl/react';
import { Squares2X2IconOutline, TableCellsIconOutline } from '@neo4j-ndl/react/icons';
import { AURA_CONSOLE_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import { SelfManagedInstances, SelfManagedInstancesCounter } from '@nx/ops';
import {
  ACTION,
  CONSUMPTION_TYPE,
  MODAL_TYPE,
  PROJECT_BILLING_METHOD,
  StoreKit,
  TIER,
  consoleApi,
  resetUploadState,
  useActiveProject,
  useActiveProjectSummary,
  useInstances,
  useModal,
  usePermissions,
  useSetting,
  useToolsConfiguration,
} from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { SearchField } from '@nx/ui';
import { skipToken } from '@reduxjs/toolkit/query';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import CloudGroup from '../assets/group-2462.svg';
import { useTrackUpxEvent } from '../services/segment/analytics';
import type { INSTANCE_DETAILS_TAB } from './instance-details';
import { InstanceDetails } from './instance-details';
import { InstanceList } from './instance-list';
import { InstanceTable } from './instance-table';

type NewInstancePageProps = {
  hasPermission: boolean;
  projectSuspended: boolean;
  tipMessage: string;
};

const NewInstance = ({ hasPermission, projectSuspended, tipMessage }: NewInstancePageProps) => {
  // We don't have trial nor free tier on Marketplace so we can't use the create flow during the onboarding flow.
  // When we introduce onboarding for Marketplace projects we want to open the create modal on onboarding completion,
  // and since we don't use the url for modals we pass an value to the state during navigation.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { state }: { state?: { openInstanceModal?: boolean } } = useLocation();

  const toolsConfiguration = useToolsConfiguration();
  const importRoute = toolsConfiguration.import?.route;
  const navigate = useNavigate();
  const createInstanceModal = useModal(MODAL_TYPE.INSTANCE_CREATE);
  const activeProject = useActiveProject();
  const multiModeInstancesActive = activeProject.capabilities.fleet_management;
  const navigateToImport = () => {
    void navigate({ pathname: `${importRoute ?? ''}` });
  };
  useEffect(() => {
    if (isNotNullish(state?.openInstanceModal) && state.openInstanceModal) {
      createInstanceModal.open({ isOnboarding: false });
      // Used to remove the state so we don't open the modal again on reload/back navigation
      window.history.replaceState({}, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div
        className={classNames('flex h-full flex-col items-center justify-center gap-16 md:flex-row', {
          'my-16': multiModeInstancesActive,
        })}
      >
        <img src={CloudGroup} alt="cloud with nodes" draggable="false" />
        <div className="flex max-w-[420px] flex-col gap-6">
          <h4>Create your first instance</h4>
          <Typography variant="body-large">
            Configure your instance and start realizing the endless possibilities with graphs.
          </Typography>
          <Tooltip type="simple" isDisabled={!projectSuspended && hasPermission}>
            <Tooltip.Trigger hasButtonWrapper>
              <Button
                onClick={() => createInstanceModal.open({ isOnboarding: false })}
                isDisabled={projectSuspended || !hasPermission}
                htmlAttributes={{
                  'data-testid': 'create-instance-button',
                }}
              >
                Create instance
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>{tipMessage}</Tooltip.Content>
          </Tooltip>
          <Typography variant="body-small">
            Have data?{' '}
            <TextLink as="button" htmlAttributes={{ onClick: navigateToImport }} className="cursor-pointer">
              Import
            </TextLink>{' '}
            existing data into Neo4j.
          </Typography>
        </div>
      </div>
    </>
  );
};

type InstanceDisplayType = 'list' | 'table';

export const AuraInstances = () => {
  const trackEvent = useTrackUpxEvent();
  const activeProject = useActiveProject();
  const { isAllowed } = usePermissions();
  const allowCreateInstance = isAllowed(ACTION.CREATE, `namespaces/${activeProject.id}/databases`);
  const [instanceDisplayType, setInstanceDisplayType] = useSetting('console', 'instanceDisplay');
  const { instances, isInstancesLoading } = useInstances({ shouldPoll: true });
  const multiModeInstancesActive = activeProject.capabilities.fleet_management;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const instanceIdParam = searchParams.get('instanceId');
  const detailsTab = searchParams.get('detailsTab');

  const upgradeModal = useModal(MODAL_TYPE.UPGRADE);
  if (!isInstancesLoading && location.hash === '#upgrade-to-trial') {
    const freeInstance = instances.find(({ tier }) => tier === TIER.FREE);
    if (
      freeInstance !== undefined &&
      allowCreateInstance &&
      freeInstance.availableActions.clone.enabled &&
      activeProject.availableActions.create_pro_trial.enabled &&
      !upgradeModal.isOpen
    ) {
      upgradeModal.open({ instance: freeInstance, project: activeProject, targetTier: TIER.PROFESSIONAL });
    }
    history.replaceState('', '', location.pathname + location.search);
  }

  const { data: usageData } = consoleApi.useGetUsageQuery(
    { projectId: activeProject.id },
    {
      skip: activeProject.billingMethod !== PROJECT_BILLING_METHOD.PAYG,
    },
  );

  const { data: consumptionData } = consoleApi.useGetConsumptionQuery(
    { projectId: activeProject.id, consumptionType: CONSUMPTION_TYPE.DATABASE },
    {
      skip: activeProject.billingMethod !== PROJECT_BILLING_METHOD.PREPAID,
    },
  );

  const consumption = consumptionData ?? usageData;

  const [instanceDetails, setInstanceDetails] = useState<{ instanceId: string; tab?: INSTANCE_DETAILS_TAB } | null>(
    null,
  );
  const handleSetInstanceDisplayType = (displayType: InstanceDisplayType) => {
    setInstanceDisplayType(displayType);
    trackEvent({
      event: AURA_CONSOLE_EVENTS.INSTANCES_DISPLAY_MODE_CHANGE,
      properties: { displayMode: displayType },
      scope: APP_SCOPE.aura,
    });
  };

  const handleDisplayInstanceDetails = (instanceId: string, tab?: INSTANCE_DETAILS_TAB) => {
    setInstanceDetails({ instanceId, tab });
    trackEvent({
      event: AURA_CONSOLE_EVENTS.INSTANCE_VIEW_DETAILS,
      scope: APP_SCOPE.aura,
    });
  };

  const handleCloseInstanceDetails = () => {
    const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
    globalStore.dispatch(resetUploadState());
    setInstanceDetails(null);
  };

  const [filter, setFilter] = useState('');
  const createInstanceModal = useModal(MODAL_TYPE.INSTANCE_CREATE);

  let createDisableReason = '';

  useEffect(() => {
    trackEvent({
      event: AURA_CONSOLE_EVENTS.INSTANCE_PAGE_NAVIGATE,
      properties: {
        planType: activeProject.planType,
        projectType: activeProject.projectType,
        projectId: activeProject.id,
      },
      scope: APP_SCOPE.aura,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNotNullish(instanceIdParam)) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      setInstanceDetails({ instanceId: instanceIdParam, tab: detailsTab as INSTANCE_DETAILS_TAB });

      // reset url so that query params dont persist and open the side modal again
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('instanceId');
      newSearchParams.delete('detailsTab');
      void navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [instanceIdParam, navigate, searchParams, detailsTab]);

  if (activeProject.suspended) {
    createDisableReason = 'Creating a new instance in a suspended project is not permitted.';
  } else if (!allowCreateInstance) {
    createDisableReason = "You don't have permission to perform this action.";
  }

  if (isInstancesLoading) {
    return (
      <LoadingSpinner
        size="large"
        className={classNames(
          { absolute: multiModeInstancesActive, relative: !multiModeInstancesActive },
          'left-1/2 top-1/2',
        )}
      />
    );
  }

  if (instances.length === 0) {
    return (
      <NewInstance
        hasPermission={allowCreateInstance}
        tipMessage={createDisableReason}
        projectSuspended={activeProject.suspended}
      />
    );
  }

  return (
    <div
      className={classNames('flex h-full flex-col', {
        'mt-2': multiModeInstancesActive,
        relative: !multiModeInstancesActive,
      })}
    >
      <div
        className={classNames('flex h-14 w-full items-center justify-between gap-4 py-[10px]', {
          'bg-neutral-bg-weak border-neutral-border-weak border-b': !multiModeInstancesActive,
          'mb-2': multiModeInstancesActive,
        })}
      >
        <div className={classNames('flex basis-[400px] gap-2', { 'ml-6': !multiModeInstancesActive })}>
          <SearchField
            className="min-w-36 grow"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            htmlAttributes={{ 'aria-label': 'Search for instances' }}
          />
        </div>
        <div className={classNames('flex shrink-0 gap-4', { 'mr-4': !multiModeInstancesActive })}>
          <SegmentedControl
            onChange={(changedId) => handleSetInstanceDisplayType(changedId)}
            hasOnlyIcons
            size="small"
            selected={instanceDisplayType}
          >
            <SegmentedControl.Item
              value="table"
              htmlAttributes={{
                'aria-label': 'View instances in Table view',
              }}
            >
              <TableCellsIconOutline className="size-4" />
            </SegmentedControl.Item>
            <SegmentedControl.Item
              value="list"
              htmlAttributes={{
                'aria-label': 'View instances in List view',
              }}
            >
              <Squares2X2IconOutline className="size-4" />
            </SegmentedControl.Item>
          </SegmentedControl>
          <Tooltip type="simple" isDisabled={!activeProject.suspended && allowCreateInstance}>
            <Tooltip.Trigger hasButtonWrapper>
              <Button
                onClick={() => createInstanceModal.open({ isOnboarding: false })}
                isDisabled={activeProject.suspended || !allowCreateInstance}
                htmlAttributes={{
                  'data-testid': 'create-instance-button',
                }}
              >
                Create instance
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>{createDisableReason}</Tooltip.Content>
          </Tooltip>
        </div>
      </div>
      {instanceDisplayType === 'list' ? (
        <div className={classNames('overflow-y-auto', { 'p-3': !multiModeInstancesActive })}>
          <InstanceList
            instances={instances}
            consumption={consumption}
            onDisplayInstanceDetails={handleDisplayInstanceDetails}
            filter={filter}
          />
        </div>
      ) : (
        <InstanceTable
          instances={instances}
          onDisplayInstanceDetails={handleDisplayInstanceDetails}
          filter={filter}
          setFilter={setFilter}
        />
      )}
      {isNotNullish(instanceDetails) && (
        <InstanceDetails
          instanceId={instanceDetails.instanceId}
          tab={instanceDetails.tab}
          onClose={handleCloseInstanceDetails}
        />
      )}
    </div>
  );
};

enum INSTANCES_TAB_VALUE {
  AURA_INSTANCES = 'aura',
  SELF_MANAGED_INSTANCES = 'self-managed',
}

const tabOptions = {
  [INSTANCES_TAB_VALUE.AURA_INSTANCES]: {
    name: 'Aura',
    component: <AuraInstances />,
    InstancesCounter: () => {
      const activeProject = useActiveProjectSummary();
      const { data, isFetching, error } = consoleApi.useListInstancesQuery(activeProject?.id ?? skipToken);

      if (isFetching && !data) {
        return (
          <div className="flex items-center">
            <Typography variant="subheading-medium">(</Typography>
            <LoadingSpinner className="" />
            <Typography variant="subheading-medium">)</Typography>
          </div>
        );
      }

      if (data && data.length > 0 && !error) {
        return <Typography variant="subheading-medium">({data.length})</Typography>;
      }

      return <Typography variant="subheading-medium">(0)</Typography>;
    },
  },
  [INSTANCES_TAB_VALUE.SELF_MANAGED_INSTANCES]: {
    name: 'Self-managed',
    component: <SelfManagedInstances />,
    InstancesCounter: SelfManagedInstancesCounter,
  },
};

export const Instances = () => {
  const [searchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState<string>(
    searchParams.get('instancesTab') ?? INSTANCES_TAB_VALUE.AURA_INSTANCES,
  );

  return (
    <div className="m-8">
      <Typography variant="h2">Instances</Typography>
      <Tabs className="mt-4" value={tabValue} onChange={setTabValue}>
        {Object.entries(tabOptions).map(([tabId, tabOption]) => (
          <Tabs.Tab key={tabId} tabId={tabId}>
            <div className="flex gap-1">
              {tabOption.name}
              <tabOption.InstancesCounter />
            </div>
          </Tabs.Tab>
        ))}
      </Tabs>
      {Object.entries(tabOptions).map(([tabId, tabOption]) => (
        <Tabs.TabPanel key={tabId} value={tabValue} tabId={tabId}>
          {tabOption.component}
        </Tabs.TabPanel>
      ))}
    </div>
  );
};

export const InstancesPage = () => {
  const activeProject = useActiveProject();
  return activeProject.capabilities.fleet_management ? <Instances /> : <AuraInstances />;
};
